import {
  AuthenticatedUserContextType,
  useUserContext,
} from '@/contexts/user-context';
import { isAddToOrderDisabled, isItemOutOfStock } from '@/utils/item';
import { useAvailabilityStatusConfig } from './use-dynamic-config-value';
import { useCallback } from 'react';
import { useVariantPackInfoContext } from '@/contexts/variant-pack-info-context';
import { VariantPackAvailabilityStatus } from '@/enums';

export const useAvailabilityStatusEnabled = () => {
  const availabilityStatusConfig = useAvailabilityStatusConfig();
  return Boolean(availabilityStatusConfig?.enabled);
};

interface ShouldRenderAvailabilityStatusLabelParams {
  availabilityStatus: VariantPackAvailabilityStatus | null;
  availableQuantity: number | null;
}

/**
 * We need to render the label if availability status is enabled, and either:
 * - The availability status is not null
 * - The item is out of stock
 */
export const useShouldRenderAvailabilityStatusLabel = ({
  availabilityStatus,
  availableQuantity,
}: ShouldRenderAvailabilityStatusLabelParams) => {
  const availabilityStatusEnabled = useAvailabilityStatusEnabled();
  const isOutOfStock = isItemOutOfStock(availableQuantity);

  return (
    availabilityStatusEnabled && (Boolean(availabilityStatus) || isOutOfStock)
  );
};

/**
 * Hook to provide a function to check if adding an item to the order is disabled.
 * This can be used by other hooks or functions that only have a variantPackUUID
 * and need to check if adding the item is disabled.
 * @returns {(variantPackUUID: string) => boolean} A function that takes a variant pack UUID and returns
 * whether adding the item is disabled.
 */
export const useGetAddToOrderDisabled = (): ((
  variantPackUUID: string,
) => boolean) => {
  const { getVariantPackInfo } = useVariantPackInfoContext();
  const availabilityStatusConfig = useAvailabilityStatusConfig();
  const userContext = useUserContext();
  const supplierEmployeeRole = userContext.isGuestMode
    ? null
    : (userContext as AuthenticatedUserContextType).supplierEmployeeRole;

  return useCallback(
    (variantPackUUID: string) => {
      const variantPackInfo = getVariantPackInfo(variantPackUUID);
      if (!variantPackInfo) {
        return false;
      }

      const { availabilityStatus, availableQuantity } = variantPackInfo;

      return isAddToOrderDisabled({
        availabilityStatus,
        availabilityStatusConfig,
        availableQuantity,
        supplierEmployeeRole,
      });
    },
    [availabilityStatusConfig, getVariantPackInfo, supplierEmployeeRole],
  );
};

/**
 * Hook to check if adding an item to the order is disabled based on its
 * availability status and quantity. This can be used by components that have
 * a variantPackUUID but need to check if adding the item is disabled based on
 * the availability status and quantity.
 * @param {string} variantPackUUID - The UUID of the variant pack.
 * @returns {boolean} True if adding the item is disabled, false otherwise.
 */
export const useAddToOrderDisabled = (variantPackUUID: string): boolean => {
  const getAddToOrderDisabled = useGetAddToOrderDisabled();
  return getAddToOrderDisabled(variantPackUUID);
};
